import React, { useEffect, useState } from "react";
import { ArrowUpRight, Check, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const PRO_FEATURES = [
    "Claude Opus 4.7 model access",
    "Unlimited custom domains + wildcard subdomains",
    "500 MB image storage (vs 20 MB free)",
    "Remove the “Built with Loom” badge from published sites",
    "Priority generation queue",
];

const FREE_FEATURES = [
    "Unlimited generations on Sonnet 4.5 & GPT-5.4",
    "1 custom domain",
    "20 MB image storage",
    "Public /s/:slug sharing with Loom badge",
    "Version history",
];

export default function PricingDialog({ open, onOpenChange, billing, onUpgraded }) {
    const [loading, setLoading] = useState(false);
    const isPro = billing?.pro;

    const startCheckout = async () => {
        setLoading(true);
        try {
            const res = await api.createCheckout("pro_monthly", window.location.origin);
            window.location.href = res.checkout_url;
        } catch (e) {
            toast.error(e.message || "Could not start checkout");
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="pricing-dialog"
                className="rounded-none border-foreground w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto loom-scroll p-0 gap-0"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="tracking-tight text-3xl font-black flex items-center gap-3">
                        <Sparkles className="w-6 h-6" strokeWidth={1.5} /> Loom Pro
                    </DialogTitle>
                    <DialogDescription>
                        Unlock the strongest model, unlimited domains, and shipped-without-a-badge sites.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                    {/* Free */}
                    <section className="p-6">
                        <div className="overline mb-4">▍ Free</div>
                        <div className="flex items-baseline gap-2">
                            <div className="text-5xl font-black tracking-tighter">$0</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                / forever
                            </div>
                        </div>
                        <ul className="mt-6 space-y-2.5">
                            {FREE_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm">
                                    <Check className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                        {!isPro && (
                            <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                ▍ You&rsquo;re on the free plan
                            </div>
                        )}
                    </section>

                    {/* Pro */}
                    <section className="p-6 bg-foreground text-background relative">
                        <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.14em] text-accent whitespace-nowrap">
                            ● Recommended
                        </div>
                        <div className="overline mb-4 !text-background/60">▍ Pro</div>
                        <div className="flex items-baseline gap-2">
                            <div className="text-5xl font-black tracking-tighter">$12</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-background/60">
                                / 30 days
                            </div>
                        </div>
                        <ul className="mt-6 space-y-2.5">
                            {PRO_FEATURES.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm">
                                    <Check className="w-4 h-4 mt-0.5 text-accent shrink-0" strokeWidth={2} />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>

                        {isPro ? (
                            <div
                                data-testid="pricing-active"
                                className="mt-8 border border-accent text-accent px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                            >
                                ▍ Active — expires {new Date(billing.pro_until).toLocaleDateString()}
                            </div>
                        ) : (
                            <button
                                data-testid="upgrade-pro-button"
                                onClick={startCheckout}
                                disabled={loading}
                                className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 px-6 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-[0.2em] hover:bg-background hover:text-foreground transition-colors disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                        Opening Stripe…
                                    </>
                                ) : (
                                    <>
                                        Upgrade for $12
                                        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                                    </>
                                )}
                            </button>
                        )}

                        <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-background/60">
                            ▍ Test mode · Card 4242 4242 4242 4242 · Any future date · Any CVC
                        </div>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
}
